import {
  addAllValuesToField,
  addFieldsToGroup,
  addSelectedField,
  addSelectedFilter,
  addValuesToGroup,
  addValueToTree,
  changeConditionalType,
  clearSelectedFields,
  clearSelectedFilters,
  removeFieldFromTree,
  removeFieldsFromGroup,
  removeSelectedField,
  removeSelectedFilter,
  removeValueFromTree,
  removeValuesFromGroup,
  replaceValueInTree,
} from 'actions/envelopes';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getFiltersSearchString,
  getSelectedFields,
  getSelectedFilters,
  getTree,
} from 'selectors/envelopes';
import { getNavParamsFromTree } from 'selectors/nav';
import { useSelector } from 'store';
import { RouteParams } from 'types';
import { Condition, DataNode, Operator, Tree, ValueNode } from 'utils/parserTree';
import { v4 } from 'uuid';

export type FiltersTreeContext = {
  tree: Tree;
  treeParams: RouteParams;
  filtersSearchString: string;
  selectedValues: ValueNode[];
  selectedFields: DataNode[];
  metricIndex?: number | null;

  handleAddValueToTree: (
    value: string,
    field: string,
    label: string,
    isSingleValue?: boolean
  ) => void;
  handleReplaceValueInTree: (
    value: string,
    field: string,
    label: string,
    isSingleValue?: boolean
  ) => void;
  handleRemoveValueFromTree: (value: string, field?: string, fieldId?: string) => void;

  handleAddAllValuesToField: (
    field: string,
    nodes: {
      value: string;
      label?: string;
    }[]
  ) => void;
  handleRemoveFieldFromTree: (field: string, id?: string) => void;

  handleChangeConditionalType: (node: DataNode, condition: Condition) => void;

  handleAddSelectedFilter: (node: ValueNode) => void;
  handleRemoveSelectedFilter: (node: ValueNode) => void;

  handleAddSelectedField: (node: DataNode) => void;
  handleRemoveSelectedField: (node: DataNode) => void;

  handleClearSelectedFields: () => void;
  handleClearSelectedFilters: () => void;

  handleAddValuesToGroup: (nodes: ValueNode[]) => void;
  handleRemoveValuesFromGroup: (nodes: ValueNode[]) => void;

  handleAddFieldsToGroup: (nodes: DataNode[]) => void;
  handleRemoveFieldsFromGroup: (nodes: DataNode[]) => void;
};

const ExploreFiltersTreeContext = React.createContext<FiltersTreeContext>({
  tree: {
    op: Operator.AND,
    data: [],
    id: v4(),
  },
  treeParams: {},
  filtersSearchString: '',
  selectedValues: [],
  selectedFields: [],

  handleAddValueToTree: () => {
    // do nothing default
  },
  handleReplaceValueInTree: () => {
    // do nothing default
  },
  handleRemoveValueFromTree: () => {
    // do nothing default
  },
  handleAddAllValuesToField: () => {
    // do nothing default
  },
  handleRemoveFieldFromTree: () => {
    // do nothing default
  },
  handleChangeConditionalType: () => {
    // do nothing default
  },
  handleAddSelectedFilter: () => {
    // do nothing default
  },
  handleRemoveSelectedFilter: () => {
    // do nothing default
  },
  handleAddSelectedField: () => {
    // do nothing default
  },
  handleRemoveSelectedField: () => {
    // do nothing default
  },
  handleClearSelectedFields: () => {
    // do nothing default
  },
  handleClearSelectedFilters: () => {
    // do nothing default
  },
  handleAddValuesToGroup: () => {
    // do nothing default
  },
  handleRemoveValuesFromGroup: () => {
    // do nothing default
  },
  handleAddFieldsToGroup: () => {
    // do nothing default
  },
  handleRemoveFieldsFromGroup: () => {
    // do nothing default
  },
});

export const ExploreFiltersTreeProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();

  const tree = useSelector(getTree);
  const treeParams = useSelector(getNavParamsFromTree);
  const filtersSearchString = useSelector(getFiltersSearchString);
  const selectedFields = useSelector(getSelectedFields);
  const selectedValues = useSelector(getSelectedFilters);

  const handleAddValueToTree = (
    value: string,
    field: string,
    label: string,
    isSingleValue = false
  ): void => {
    dispatch(
      addValueToTree({
        value,
        field,
        label,
        isSingleValue,
      })
    );
  };

  const handleReplaceValueInTree = (
    value: string,
    field: string,
    label: string,
    isSingleValue = false
  ): void => {
    dispatch(
      replaceValueInTree({
        value,
        field,
        label,
        isSingleValue,
      })
    );
  };

  const handleRemoveValueFromTree = (value: string, field?: string, fieldId?: string): void => {
    dispatch(removeValueFromTree({ value, field, fieldId }));
  };

  const handleAddAllValuesToField = (
    field: string,
    nodes: { value: string; label?: string }[]
  ): void => {
    dispatch(
      addAllValuesToField({
        field,
        nodes,
      })
    );
  };

  const handleRemoveFieldFromTree = (field: string, id?: string): void => {
    dispatch(removeFieldFromTree({ field, id }));
  };

  const handleChangeConditionalType = (node: DataNode, condition: Condition): void => {
    dispatch(changeConditionalType({ node, condition }));
  };

  const handleAddSelectedFilter = (node: ValueNode): void => {
    dispatch(addSelectedFilter({ node }));
  };

  const handleRemoveSelectedFilter = (node: ValueNode): void => {
    dispatch(removeSelectedFilter({ node }));
  };

  const handleAddSelectedField = (node: DataNode): void => {
    dispatch(addSelectedField({ node }));
  };

  const handleRemoveSelectedField = (node: DataNode): void => {
    dispatch(removeSelectedField({ node }));
  };

  const handleClearSelectedFields = (): void => {
    dispatch(clearSelectedFields());
  };

  const handleClearSelectedFilters = (): void => {
    dispatch(clearSelectedFilters());
  };
  const handleAddValuesToGroup = (nodes: ValueNode[]): void => {
    dispatch(addValuesToGroup({ nodes }));
  };

  const handleRemoveValuesFromGroup = (nodes: ValueNode[]): void => {
    dispatch(removeValuesFromGroup({ nodes }));
  };

  const handleAddFieldsToGroup = (nodes: DataNode[]): void => {
    dispatch(addFieldsToGroup({ nodes }));
  };

  const handleRemoveFieldsFromGroup = (nodes: DataNode[]): void => {
    dispatch(removeFieldsFromGroup({ nodes }));
  };

  const value = {
    tree,
    treeParams,
    filtersSearchString,
    selectedFields,
    selectedValues,

    handleAddValueToTree,
    handleReplaceValueInTree,
    handleRemoveValueFromTree,
    handleAddAllValuesToField,
    handleRemoveFieldFromTree,
    handleChangeConditionalType,
    handleAddSelectedFilter,
    handleRemoveSelectedFilter,
    handleAddSelectedField,
    handleRemoveSelectedField,
    handleClearSelectedFields,
    handleClearSelectedFilters,

    handleAddValuesToGroup,
    handleRemoveValuesFromGroup,
    handleAddFieldsToGroup,
    handleRemoveFieldsFromGroup,
  };

  return (
    <ExploreFiltersTreeContext.Provider value={value}>
      {children}
    </ExploreFiltersTreeContext.Provider>
  );
};

export default ExploreFiltersTreeContext;
